import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Observable, observable, throwError, from, of } from 'rxjs';
import { environment } from 'src/app/environments/environments';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { NotificationService } from 'src/app/shared/services/notification.service';


const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};
const httpHeader = {
  headers: new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }),
};
const HeaderConnect = {
  headers: new HttpHeaders({}),
};

@Injectable({
  providedIn: 'root',
})
export class RemoteApiService {
  urlRecommender = environment.urlRecommender;
  facebookBaseUrl =
    'https://graph.facebook.com/' + environment.facebookVersion + '/';
  stripeSubscriptionApiHost =
    'https://us-central1-conversell-258914.cloudfunctions.net/stripeSubscription';
  urlMessengerProfile = this.facebookBaseUrl + 'me/messenger_profile';
  urlGetStarted = this.facebookBaseUrl + 'me/messenger_profile';
  urlCreateLabel = this.facebookBaseUrl + 'me/custom_labels?access_token=';
  urlPersistentMenu = this.facebookBaseUrl + 'me/messenger_profile';
  urlStartedMessage = this.facebookBaseUrl + 'me/messenger_profile';
  urlMessengerProfileForInsta =
    this.facebookBaseUrl + 'me/messenger_profile?platform=instagram';
  /** Graph path for WhatsApp Cloud API sends (`POST /{phone-number-id}/messages`) — token is server-side only. */
  whatsappMessagesPath(phoneNumberId: string): string {
    const id = encodeURIComponent(String(phoneNumberId || '').trim());
    return `${this.facebookBaseUrl}${id}/messages`;
  }
  urlPersonas = this.facebookBaseUrl + 'me/personas';
  shortToken =
    'https://graph.facebook.com/oauth/access_token?client_id=' +
    environment.facebookAppKey +
    '&client_secret=' +
    environment.facebookAppSecretKey +
    '&grant_type=fb_exchange_token&fb_exchange_token=';
  mediaAttachmentUrl = `https://graph.facebook.com/${environment.facebookVersion}/me/message_attachments?access_token=`;
  facetsUrl = environment.urlFacets;
  updateFacetUrl = environment.updateFacetUrl;
  updateFacetsRankUrl = environment.updateFacetsRankUrl;
  urlSubscription =
    'https://admin.favizone.com/messenger/subscribeConvAppToPage';
  urlUnsubscription =
    'https://admin.favizone.com/messenger/unsubscribeAppFromPage';
  urlSetFields = this.facebookBaseUrl + 'me/subscribed_apps';
  commonHeaders = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  facebookPostAPI(url, reqBody, token, silent = false) {
    let sign;
    sign = url.indexOf('?') > -1 ? (sign = '&') : '?';
    return this.http
      .post(url + sign + 'access_token=' + token, reqBody, httpOptions)
      .pipe(
        catchError((error) =>
          silent ? throwError(() => error) : this.handleFacebookApiError(error),
        ),
      );
  }

  /**
   * Graph POST as application/x-www-form-urlencoded (no JSON preflight).
   * Prefer this for endpoints that fail CORS from the browser with JSON bodies
   * (e.g. Personas API on localhost).
   */
  facebookPostFormAPI(
    url: string,
    reqBody: Record<string, string>,
    token: string,
    silent = false,
  ) {
    const body = new HttpParams({
      fromObject: {
        ...reqBody,
        access_token: token,
      },
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http
      .post(url, body.toString(), { headers })
      .pipe(
        catchError((error) =>
          silent ? throwError(() => error) : this.handleFacebookApiError(error),
        ),
      );
  }
facebookDeleteAPI(reqBody, token) {
  const fields = JSON.stringify(reqBody.split(',').map(f => f.trim()));
  const url = this.urlMessengerProfile + '?access_token=' + token + `&fields=${encodeURIComponent(fields)}`;
  return this.http.delete(url)
    .pipe(catchError(this.handleError));
}

  /**
   * Messenger Profile API DELETE — uses query `fields` param (works from browser).
   * @see https://developers.facebook.com/docs/messenger-platform/reference/messenger-profile-api
   */
  facebookDeleteMessengerProfileFields(
    fields: string[],
    token: string,
    useInstagramPlatform = false,
    silent = false,
  ) {
    return this.facebookDeleteMessengerProfile(
      JSON.stringify(fields),
      token,
      useInstagramPlatform,
      silent,
    );
  }

  /** Messenger Profile API delete; use `instagram` for Instagram messaging surface (`platform=instagram`). */
  facebookDeleteMessengerProfile(
    reqBody: string,
    token: string,
    useInstagramPlatform = false,
    silent = false,
  ) {
    const base = useInstagramPlatform
      ? this.urlMessengerProfileForInsta
      : this.urlMessengerProfile;
    const sep = base.includes('?') ? '&' : '?';
    const encodedToken = encodeURIComponent(token);
    const encodedFields = encodeURIComponent(reqBody);
    return this.http
      .delete(
        `${base}${sep}access_token=${encodedToken}&fields=${encodedFields}`,
      )
      .pipe(
        catchError((error) =>
          silent ? throwError(() => error) : this.handleFacebookApiError(error),
        ),
      );
  }

  private httpPost(url: string, body: any, options: any) {
    return this.http
      .post(url, body, options)
      .pipe(catchError(this.handleError));
  }

  private httpDelete(url: string, options: any) {
    return this.http.delete(url, options).pipe(catchError(this.handleError));
  }

  importAdavncedTheme(url: string, body: any) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }

  deleteIntent(urlDelete: string, dailogFlowToken: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + dailogFlowToken,
      }),
    };
    return this.http
      .delete(urlDelete, httRecpOptions)
      .pipe(catchError(this.handleError));
  }

  addIntent(url: string, token: string, body: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + token,
      }),
    };

    return this.http
      .post(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  createKnowldge(url: string, accessToken: string, body: string): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .post(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  addReplayIntent(url: string, token: string, body: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + token,
      }),
    };

    return this.http
      .post(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  getAgent(urlGet: string, token: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + token,
      }),
    };
    return this.http
      .get(urlGet, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  createBot(url: string, body: any): Observable<any> {
    return this.postApi(url, body).pipe(
      map((response: any) => {
        const botId =
          response?.result?.botId ??
          response?.result?.bot?.id ??
          response?.data?.botId ??
          response?.result?.data?.botId;
        if (response?.success && botId) {
          return response;
        }
        return {
          success: false,
          data: null,
          message:
            response?.message || response?.error || 'Failed to create bot',
        };
      }),
      catchError((error: any) => {
        return of({
          success: false,
          data: null,
          message:
            error?.error?.error || error?.message || 'Failed to create bot',
        });
      }),
    );
  }
  updateIntent(url: string, accessToken: string, body: any): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .patch(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  updateReplayIntent(url: string, accessToken: string, body: any): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .patch(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  qrCodeApi(token: string, data: any) {
    let url =
      'https://graph.facebook.com/' +
      environment.facebookVersion +
      '/me/messenger_codes?access_token=' +
      token;

    return this.http
      .post(url, data, httpHeader)
      .pipe(catchError(this.handleError));
  }
  createStripeSubscription(body: any) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
      }),
    };
    return this.http
      .post(
        this.stripeSubscriptionApiHost + '/create-subscription',
        body,
        httRecpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  /** Arrow so `this` stays bound when used as `catchError(this.handleFacebookApiError)`. */
  private handleFacebookApiError = (error: HttpErrorResponse) => {
    console.log('Something went wrong', error);
    return throwError(() => error);
  };

  /** Arrow so `this` stays bound when used as `catchError(this.handleError)`. */
  private handleError = (error: HttpErrorResponse) => {
    console.log('Something went wrong', error);
    return throwError(() => error);
  };

  constructor(
    private http: HttpClient,
    private afAuth: AngularFireAuth,
    private notificationService: NotificationService,
  ) {}

  recommenderApi(shopId: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        shopId: shopId,
      }),
    };
    return this.http
      .get(this.urlRecommender, httRecpOptions)
      .pipe(catchError(this.handleError));
  }

  meneuAPI(reqBody: any, token: string) {
    return this.http
      .post(
        this.urlPersistentMenu + '?access_token=' + token,
        reqBody,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  getStartedAPI(reqBody: any, token: string) {
    return this.http
      .post(this.urlGetStarted + '?access_token=' + token, reqBody, httpOptions)
      .pipe(catchError(this.handleError));
  }

  createLablel(token: string, name: string) {
    return this.http
      .post(this.urlCreateLabel + token + '&name=' + name, httpOptions)
      .pipe(catchError(this.handleError));
  }

  pageSubscriptionAPI(token: string, pageId: string) {
    const reqBody = {
      facebookData: {
        access_token: token,
        id: pageId,
      },
      version: environment.facebookVersion,
      fbAppId: environment.facebookAppKey,
      fbAppSecretKey: environment.facebookAppSecretKey,
    };
    return this.http
      .post(this.urlSubscription, reqBody, httpOptions)
      .pipe(catchError(this.handleError));
  }

  pageUnsubscription(token: string, pageId: string) {
    const reqBody = {
      facebookData: {
        access_token: token,
        id: pageId,
      },
      version: environment.facebookVersion,
    };
    return this.http
      .post(this.urlUnsubscription, reqBody, httpOptions)
      .pipe(catchError(this.handleError));
  }

  messageApi(message$: string) {
    return this.http
      .post(this.urlStartedMessage, message$, httpOptions)
      .pipe(catchError(this.handleError));
  }

  /** Complete WhatsApp Embedded Signup v4 (code TTL is 30s — call immediately). */
  completeWhatsappEmbeddedSignup(body: {
    botId: string;
    code: string;
    wabaId: string;
    phoneNumberId?: string;
    businessId?: string;
    event?: string;
    redirectUri?: string;
  }) {
    const url = `${environment.botManagerUrl}/whatsappEmbeddedSignup`;
    const sendRequest = (options: any) =>
      this.http
        .post(url, body, options)
        .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(httpHeader);
    }
    return from(this.buildHeadersWithAuth(httpHeader.headers)).pipe(
      switchMap((headers) => sendRequest({ ...httpHeader, headers })),
    );
  }

  /** Subscribe this Meta app to a WhatsApp Business Account webhook. */
  subscribeWhatsappWaba(wabaId: string, token: string) {
    const body = new HttpParams({
      fromObject: {
        access_token: String(token),
      },
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http
      .post(
        `${this.facebookBaseUrl}${encodeURIComponent(wabaId)}/subscribed_apps`,
        body.toString(),
        { headers },
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * `messaging_policy_enforcement` is how Meta reports a platform-policy violation before it
   * restricts access. Subscribing the page is only half of it — the field must also be checked
   * under App Dashboard > Webhooks > Page (and Instagram).
   */
  setFields(pageId, token, botType?: string, silent = false) {
    const messengerFields =
      'messages,messaging_postbacks,messaging_optins,messaging_account_linking,messaging_feedback,messaging_policy_enforcement';
    const instagramFields =
      'messages,messaging_postbacks,messaging_optins,messaging_policy_enforcement';
    const fields = botType === 'instagram' ? instagramFields : messengerFields;

    // Form-urlencoded avoids a JSON CORS preflight; Graph rejects Content-Type: application/json.
    const body = new HttpParams({
      fromObject: {
        subscribed_fields: fields,
        access_token: String(token),
      },
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http
      .post(
        `${this.facebookBaseUrl}${encodeURIComponent(pageId)}/subscribed_apps`,
        body.toString(),
        { headers },
      )
      .pipe(
        catchError((error) =>
          silent ? throwError(() => error) : this.handleError(error),
        ),
      );
  }

  /**
   * Replay a synthetic `messaging_policy_enforcement` notice through the ChannelManager, used by
   * the admin Policy Enforcement screen to exercise the pipeline end to end.
   *
   * The notice travels the real webhook path — interception, journal write, compliance email —
   * so the journal keeps its property of being written only by the backend. The `_simulated`
   * marker is not a Meta field; the service uses it to flag the row and prefix the email subject
   * with [TEST], so a drill is never mistaken for a live enforcement.
   *
   * `x-widgetchat-user-agent` carries the channel hint because browsers refuse to set
   * `user-agent`; `detectChannel` accepts either (see channelManager.controller).
   */
  simulatePolicyEnforcement({
    channel,
    entryId,
    action,
    reason,
  }: {
    channel: 'messenger' | 'instagram';
    entryId: string;
    action: string;
    reason: string;
  }) {
    const body = {
      object: channel === 'instagram' ? 'instagram' : 'page',
      entry: [
        {
          id: String(entryId),
          time: Date.now(),
          messaging: [
            {
              recipient: { id: String(entryId) },
              timestamp: Date.now(),
              'policy-enforcement': {
                action: String(action),
                reason: String(reason),
                _simulated: true,
              },
            },
          ],
        },
      ],
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-widgetchat-user-agent': 'facebookexternalua',
    });

    const base = String(environment.channelManagerUrl || '').replace(/\/$/, '');
    return this.http
      .post(`${base}/manage_channel`, body, { headers, responseType: 'text' })
      .pipe(catchError(this.handleError));
  }

  UnsubscribeApptoWebhookFacebookPage(pageId, token) {
    return this.http
      .delete(
        this.facebookBaseUrl +
          pageId +
          `/subscribed_apps?access_token=` +
          token,
        httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  CheckConnectToFavizone() {
    let url = 'https://admin.favizone.com/connect-to-Conversell';
    const res = this.http.get(url, HeaderConnect);
    return res.pipe(catchError(this.handleError));
  }
  ConnectFavizone(body: any) {
    let url = 'https://admin.favizone.com/conversell-login';

    return this.http
      .post(url, body, httpOptions)
      .pipe(catchError(this.handleError));
  }
  facetsApi(shopId: string) {
    const facetsHttpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'authorization': 'Basic RkFWSVpPTkVfUkVNT1RFX1VTRVI6d0NtazRaTidNMkh4KV9SYg=='
      }),
    };
    return this.http
      .get(this.facetsUrl + '/' + shopId, facetsHttpOptions)
      .pipe(catchError(this.handleError));
  }

  updateFacetApi(shopId: string, facet: any) {
    const facetsHttpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'authorization': 'Basic RkFWSVpPTkVfUkVNT1RFX1VTRVI6d0NtazRaTidNMkh4KV9SYg=='
      }),
    };
    return this.http
      .post(this.updateFacetUrl + '/' + shopId, facet, facetsHttpOptions)
      .pipe(catchError(this.handleError));
  }

  updateFacetsRankApi(shopId: string, facets: any) {
    const facetsHttpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'authorization': 'Basic RkFWSVpPTkVfUkVNT1RFX1VTRVI6d0NtazRaTidNMkh4KV9SYg=='
      }),
    };
    return this.http
      .post(this.updateFacetsRankUrl + '/' + shopId, facets, facetsHttpOptions)
      .pipe(catchError(this.handleError));
  }
  saveFlowData(body: any) {
    //
    let url = 'https://admin.favizone.com/api/update-import-data';

    return this.http
      .post(url, body, httpOptions)
      .pipe(catchError(this.handleError));
  }
  favizoneConnection(reqBody: any) {
    return this.http
      .post(environment.favizoneAccountUrl, reqBody, httpOptions)
      .pipe(catchError(this.handleError));
  }
  getAttachmentId(token: string, body: any) {
    return this.http
      .post(this.mediaAttachmentUrl + token, body, httpOptions)
      .pipe(catchError(this.handleError));
  }

  simpleGet(url: string) {

    const sendRequest = (options?: any) =>
      this.http.get(url, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest();
    }

    return from(this.buildHeadersWithAuth(new HttpHeaders())).pipe(
      switchMap((headers) => sendRequest({ headers })),
    );
  }
  // New Dialogflow
  // Post
  postDialogflow(url: string, accessToken: string, body: any): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .post(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  // Get
  getDialogflow(url: string, accessToken: string): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .get(url, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  // Put
  putDialogflow(url: string, accessToken: string, body: any): any {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .put(url, body, httRecpOptions)
      .pipe(catchError(this.handleError));
  }
  // delete
  deleteDialogflow(url: string, accessToken: string) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
        Authorization: 'Bearer ' + accessToken,
      }),
    };
    return this.http
      .delete(url, httRecpOptions)
      .pipe(catchError(this.handleError));
  }

  // New Normal text/json
  getApi(url: string, silent = false): any {
    const onError = silent
      ? (error: HttpErrorResponse) => throwError(() => error)
      : this.handleError;
    const sendRequest = (options: any) =>
      this.http.get(url, options).pipe(catchError(onError));

    const baseOptions = this.buildGetOptions(url);

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  // GET with query params
  getApiWithQueryParams(url: string, queryParameters: any, silent = false): any {
    const onError = silent
      ? (error: HttpErrorResponse) => throwError(() => error)
      : this.handleError;
    const sendRequest = (options: any) =>
      this.http.get(url, options).pipe(catchError(onError));

    const baseOptions = {
      ...this.buildGetOptions(url),
      params: new HttpParams({ fromObject: queryParameters }),
    };

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }

  // Post
  postApi(url: string, body: any): any {
    const sendRequest = (options: any) =>
      this.http.post(url, body, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(httpHeader);
    }

    return from(this.buildHeadersWithAuth(httpHeader.headers)).pipe(
      switchMap((headers) => sendRequest({ ...httpHeader, headers })),
    );
  }
  postApiWithFormData(url: string, formData: FormData): Observable<any> {
    const sendRequest = (authorization?: string) => {
      // Do not set Content-Type: the browser must add multipart boundary.
      const options = authorization
        ? { headers: { Authorization: authorization } }
        : {};
      return this.http
        .post(url, formData, options)
        .pipe(catchError(this.handleError));
    };

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest();
    }

    return from(this.buildHeadersWithAuth(new HttpHeaders())).pipe(
      switchMap((headersWithAuth) => {
        const authorization = headersWithAuth.get('Authorization') || undefined;
        return sendRequest(authorization);
      }),
    );
  }
  // Put
  putApi(url: string, body: any): any {
    const sendRequest = (options: any) =>
      this.http.put(url, body, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(httpHeader);
    }

    return from(this.buildHeadersWithAuth(httpHeader.headers)).pipe(
      switchMap((headers) => sendRequest({ ...httpHeader, headers })),
    );
  }

  patchApi(url: string, body: any): any {
    const sendRequest = (options: any) =>
      this.http.patch(url, body, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(httpHeader);
    }

    return from(this.buildHeadersWithAuth(httpHeader.headers)).pipe(
      switchMap((headers) => sendRequest({ ...httpHeader, headers })),
    );
  }

  postApiWithParams(url: string, queryParameters: any, body: any): any {
    let params = new HttpParams();
    for (const key in queryParameters) {
      if (queryParameters.hasOwnProperty(key)) {
        params = params.append(key, queryParameters[key]);
      }
    }
    const baseOptions: any = { params };
    const sendRequest = (options: any) =>
      this.http.post(url, body, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(httpHeader.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }

  /**
   * Graph GET from the browser must not send Content-Type: application/json.
   * That header triggers a CORS preflight that Facebook does not allow
   * (`content-type` is missing from Access-Control-Allow-Headers).
   */
  private isFacebookGraphUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    try {
      const host = new URL(url).hostname.toLowerCase();
      return host === 'graph.facebook.com' || host === 'graph.instagram.com';
    } catch {
      return url.includes('graph.facebook.com') || url.includes('graph.instagram.com');
    }
  }

  private buildGetOptions(url: string): { headers?: HttpHeaders } {
    if (this.isFacebookGraphUrl(url)) {
      return {};
    }
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
  }

  private shouldAttachIdToken(url: string): boolean {
    if (!url) {
      return false;
    }

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return false;
    }

    if (trimmedUrl.startsWith('/')) {
      return true;
    }

    try {
      const parsedUrl = new URL(trimmedUrl);
      const href = parsedUrl.href.toLowerCase();
      const host = parsedUrl.hostname.toLowerCase();
      if (host === 'graph.facebook.com' || host === 'graph.instagram.com') {
        return false;
      }
      if (host === 'admin.conversell.ai') {
        return true;
      }
      if (href.includes('conversell') && href.includes('gateway')) {
        return true;
      }
      if (host === '127.0.0.1' || host === 'localhost') {
        const path = parsedUrl.pathname.toLowerCase();
        return (
          path.includes('administratormanager') ||
          path.includes('usermanager') ||
          path.includes('botmanager')
        );
      }
      return false;
    } catch {
      return false;
    }
  }

  private shouldAttachIdTokenAlexa(url: string): boolean {
    if (!url) return false;

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return false;

    if (trimmedUrl.startsWith('/')) return true;

    try {
      const parsed = new URL(trimmedUrl);
      const host = parsed.hostname.toLowerCase();
      const path = parsed.pathname.toLowerCase();

      const isLocalFunctionsEmulator =
        host === '127.0.0.1' || host === 'localhost';

      return (
        host === 'alexabackend-rcipzn3pdq-uc.a.run.app' ||
        host === 'resizeiconalexa-rcipzn3pdq-uc.a.run.app' ||
        host === 'us-central1-conversell-3a451.cloudfunctions.net' ||
        isLocalFunctionsEmulator ||
        path.includes('/auth') ||
        path.includes('/create/') ||
        path.includes('/update/') ||
        path.includes('/delete/') ||
        path.includes('/resize') ||
        (host.includes('conversell') && host.includes('gateway'))
      );
    } catch {
      return false;
    }
  }
  private async buildHeadersWithAuthAlexa(
    baseHeaders?: HttpHeaders,
  ): Promise<HttpHeaders> {
    const headers = baseHeaders ?? new HttpHeaders();

    const user = await this.afAuth.currentUser;
    if (!user) {
      throw new Error('No Firebase user session');
    }

    const idToken = await user.getIdToken(true); // force refresh
    if (!idToken) {
      throw new Error('Unable to get Firebase ID token');
    }

    return headers.set('Authorization', `Bearer ${idToken}`);
  }

  private async buildHeadersWithAuth(
    baseHeaders?: HttpHeaders,
  ): Promise<HttpHeaders> {
    const headers = baseHeaders ?? new HttpHeaders();
    try {
      const user = await this.afAuth.currentUser;
      if (!user) {
        return headers;
      }

      const idToken = await user.getIdToken(true);
      if (!idToken) {
        return headers;
      }
      return headers.set('Authorization', `Bearer ${idToken}`);
    } catch (error) {
      this.notificationService.showWarning("Unable to append Firebase ID token to request headers");
      return headers;
    }
  }
  postApiWithBody(url: string, body: any): any {

    const sendRequest = (options: any) =>
      this.http.post(url, body, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest({});
    }

    return from(this.buildHeadersWithAuth(new HttpHeaders())).pipe(
      switchMap((headers) => sendRequest({ headers })),
    );
  }
  postApiWithQueryParams(url: string, queryParameters: any): any {

    let params = new HttpParams();
    for (const key in queryParameters) {
      if (queryParameters.hasOwnProperty(key)) {
        params = params.append(key, queryParameters[key]);
      }
    }

    const baseOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: params,
    };

    const sendRequest = (options: any) =>
      this.http.post(url, null, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  // PUT with QueryParams
  putApiWithQueryParams(url: string, params: any): Observable<any> {

    let queryParams = new HttpParams();
    // Construction des paramètres de requête à partir de l'objet params
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        queryParams = queryParams.append(key, params[key]);
      }
    }

    const baseOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: queryParams,
    };

    const sendRequest = (options: any) =>
      this.http.put(url, null, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  /* putApiWithQueryParams(url: string,  queryParameters: any ): any {
     console.log( 'TEST',  url)
     let params = new HttpParams();
     for (const key in queryParameters) {
       if (queryParameters.hasOwnProperty(key)) {
         params = params.append(key, queryParameters[key]);
       }
     }
     const httRecpOptions = {
       headers: new HttpHeaders({
         'Content-Type':'application/json',
       })
     };
     console.log('params',params)
     console.log('url',url)
     return this.http.put(url, { params })
     .pipe(
       catchError(this.handleError)
     );
   }*/

  deleteApiWithParams(url: string, queryParameters: any) {

    let params = new HttpParams();
    for (const key in queryParameters) {
      if (queryParameters.hasOwnProperty(key)) {
        params = params.append(key, queryParameters[key]);
      }
    }

    const baseOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      params: params,
    };

    const sendRequest = (options: any) =>
      this.http.delete(url, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  deleteApiWithBody(url: string, body: any) {

    const baseOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: body,
    };

    const sendRequest = (options: any) =>
      this.http
        .request('DELETE', url, options)
        .pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  updateFiles(
    putUrl: string,
    queryParams: any,
    formData: FormData,
  ): Observable<any> {

    let params = new HttpParams();
    for (const key in queryParams) {
      if (queryParams.hasOwnProperty(key)) {
        params = params.set(key, queryParams[key]);
      }
    }

    const sendRequest = (headers?: HttpHeaders) => {
      const options: any = { params: params };

      // For FormData, don't set Content-Type (browser will set it with boundary)
      if (headers && !headers.has('Content-Type')) {
        options.headers = headers;
      } else if (headers) {
        // Remove Content-Type if it exists for FormData
        const headersWithoutContentType = headers.delete('Content-Type');
        options.headers = headersWithoutContentType;
      }

      return this.http
        .put(putUrl, formData, options)
        .pipe(catchError(this.handleError));
    };

    if (!this.shouldAttachIdToken(putUrl)) {
      return sendRequest();
    }

    return from(this.buildHeadersWithAuth(new HttpHeaders())).pipe(
      switchMap((headersWithAuth) => {
        // Remove Content-Type for FormData
        if (headersWithAuth.has('Content-Type')) {
          headersWithAuth = headersWithAuth.delete('Content-Type');
        }
        return sendRequest(headersWithAuth);
      }),
    );
  }
  // delete
  deleteApi(url: string) {

    const baseOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    const sendRequest = (options: any) =>
      this.http.delete(url, options).pipe(
        catchError((error: HttpErrorResponse) => {
          this.handleError(error);
          return throwError(() => error);
        }),
      );

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(baseOptions.headers)).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  getfileContent(url: string): any {

    const baseOptions = {
      responseType: 'text' as const,
    };

    const sendRequest = (options: any) =>
      this.http.get(url, options).pipe(catchError(this.handleError));

    if (!this.shouldAttachIdToken(url)) {
      return sendRequest(baseOptions);
    }

    return from(this.buildHeadersWithAuth(new HttpHeaders())).pipe(
      switchMap((headers) => sendRequest({ ...baseOptions, headers })),
    );
  }
  getStripePaymentPortailSession(body: any) {
    const httRecpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json ; charset=UTF-8',
      }),
    };
    return this.http
      .post(
        this.stripeSubscriptionApiHost + '/create-customer-portal-session',
        body,
        httRecpOptions,
      )
      .pipe(catchError(this.handleError));
  }
  getPageAccessToken(pageId, accessToken): any {
    return this.http
      .get(
        this.facebookBaseUrl +
          pageId +
          '?fields=access_token&access_token=' +
          accessToken,
      )
      .pipe(catchError(this.handleError));
  }

  postApiAlexa(url: string, body: any): any {
    const sendRequest = (options: any) =>
      this.http.post(url, body, options).pipe(catchError(this.handleError));
    if (!this.shouldAttachIdTokenAlexa(url)) {
      return sendRequest(httpHeader);
    }

    return from(this.buildHeadersWithAuthAlexa(httpHeader.headers)).pipe(
      switchMap((headers) => {
        return sendRequest({ ...httpHeader, headers });
      }),
    );
  }
  /**
   * Read the page token from /me/accounts. Unlike GET /{page-id}?fields=access_token,
   * this only needs pages_show_list (no pages_read_engagement / Page Public Metadata Access).
   */
  getPageTokenFromAccounts(
    pageId: string,
    userAccessToken: string,
    silent = false,
  ): any {
    const url =
      this.facebookBaseUrl +
      'me/accounts?limit=200&fields=id,name,access_token,instagram_business_account&access_token=' +
      encodeURIComponent(userAccessToken);

    // No Content-Type: Graph GET + application/json is blocked by CORS.
    return this.http.get(url).pipe(
      map((res: any) => {
        const page = (res?.data || []).find(
          (p: any) => String(p.id) === String(pageId),
        );
        if (!page?.access_token) {
          throw new Error(
            'This Facebook page is not available on your account. Please reconnect and grant access to it.',
          );
        }
        return {
          access_token: page.access_token,
          name: page.name,
          instagram_business_account: page.instagram_business_account,
        };
      }),
      catchError((error) =>
        silent ? throwError(() => error) : this.handleError(error),
      ),
    );
  }

  /**
   * Exchange a short-lived user/page token for a long-lived token via the backend.
   * Never call graph.facebook.com/oauth/access_token from the browser — it requires
   * the app secret and is blocked by CORS.
   */
  exchangePageToken(pageAccessToken: string): any {
    return this.getApi(
      `${environment.botManagerUrl}/exchangeFbToken?shortUserAccessToken=${encodeURIComponent(pageAccessToken)}`,
    );
  }
}
